// react components
import React, {
    useEffect,
    useState,
} from 'react'
import axios from 'axios'
import {
    useDispatch,
    useSelector,
} from 'react-redux'

// components
import {
    LinkHelperSite,
} from 'components'

// data
import {
    defaultReduxState,
    reduxModalErrorEventHandlerSite,
} from 'data'

// pages
import {
    EditBlockSite,
} from 'pages'

// serializers
import {
    CustomCSSProperties,
    MainStyleSerializer,
    PortfolioPageContentListSiteSerializer,
    StripeCustomer826Helper,
    Template826SiteSerializer,
} from 'serializers/site'

// services
import {
    axiosErrorHandlerSite,
    getApiUrlSiteV2,
    getAxiosHeadersSite,
    getStylesNew,
} from 'services'

// props
type TemplateBlock826SiteProps = {
    blockId: string
    content: Template826SiteSerializer | undefined
    isEditHovered: boolean
    isInComponent?: boolean
    mainParentId?: number
    object: PortfolioPageContentListSiteSerializer
    parentArray?: number[]
    parentStyles?: any
    styles: MainStyleSerializer
    stylesEdit: MainStyleSerializer | undefined
}

// main
export const TemplateBlock826Site: React.FC<TemplateBlock826SiteProps> = React.memo(({
    blockId,
    content,
    isEditHovered,
    isInComponent,
    mainParentId,
    object,
    parentArray,
    parentStyles,
    styles,
    stylesEdit,
}) => {


    return (
        <>
            {content?.stripe_customers?.map((customer) => (
                <TemplateBlock826HelperSite
                    key={customer.id}
                    blockId={blockId}
                    content={content}
                    customer={customer}
                    isEditHovered={isEditHovered}
                    isInComponent={isInComponent}
                    mainParentId={mainParentId}
                    object={object}
                    parentArray={parentArray}
                    parentStyles={parentStyles}
                    styles={styles}
                    stylesEdit={stylesEdit}
                />
            ))}
        </>
    )
})

// helpers

// props
type TemplateBlock826HelperSiteProps = {
    blockId: string
    content: Template826SiteSerializer | undefined
    customer: StripeCustomer826Helper
    isEditHovered: boolean
    isInComponent?: boolean
    mainParentId?: number
    object: PortfolioPageContentListSiteSerializer
    parentArray?: number[]
    parentStyles?: any
    styles: MainStyleSerializer
    stylesEdit: MainStyleSerializer | undefined
}

// main
export const TemplateBlock826HelperSite: React.FC<TemplateBlock826HelperSiteProps> = React.memo(({
    blockId,
    content,
    customer,
    isEditHovered,
    isInComponent,
    mainParentId,
    object,
    parentArray,
    parentStyles,
    styles,
    stylesEdit,
}) => {

    const dispatch = useDispatch()
    const reduxAuth = useSelector((state: defaultReduxState) => state.reduxAuth)
    const reduxCacheSiteportfolioid = useSelector((state: defaultReduxState) => state.reduxCacheSite.portfolio?.id)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)

    const deviceType = reduxModalSite.deviceType

    const [portalLoading, setPortalLoading] = useState<{ [key: number]: boolean }>({})
    const [stylesNew, setStylesNew] = useState<{
        buttonStyle: CustomCSSProperties | undefined,
        hoverStyleBackground: string | undefined,
        hoverStyleBorderColor: string | undefined,
        hoverStyleColor: string | undefined,
        hasHoverStyle: boolean,
    } | undefined>(applyStyles())
    const [newStyles, setNewStyles] = useState<any>(stylesNew?.buttonStyle)

    useEffect(() => {
        setStylesNew(applyStyles())
    }, [
        object.id,
        reduxModalSite,
        styles,
        stylesEdit,
    ])

    useEffect(() => {
        const tempStyles = applyStyles()
        setStylesNew(tempStyles)
        setNewStyles(applyStyles()?.buttonStyle)
    }, [
        object.id,
        reduxModalSite,
        styles,
        stylesEdit,
    ])

    function applyStyles() {
        try {
            const buttonStyle = getStylesNew(reduxModalSite, styles, stylesEdit)
            const hoverStyleBackground = buttonStyle?.hoverBackground
            const hoverStyleBorderColor = buttonStyle?.hoverBorderColor
            const hoverStyleColor = buttonStyle?.hoverColor
            const hasHoverStyle = Boolean(hoverStyleBackground || hoverStyleBorderColor || hoverStyleColor)
            return {
                buttonStyle: buttonStyle,
                hoverStyleBackground: hoverStyleBackground,
                hoverStyleBorderColor: hoverStyleBorderColor,
                hoverStyleColor: hoverStyleColor,
                hasHoverStyle: hasHoverStyle,
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'TemplateBlock826HelperSite',
                'applyStyles',
            ))
        }
    }

    async function handleStripePortal(customer: StripeCustomer826Helper) {
        try {
            setPortalLoading(prev => ({ ...prev, [customer.id]: true }))
            const refreshAxiosHeaders = await getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch)
            const postUrl = getApiUrlSiteV2('sales/stripe/portal/create/', reduxModalSite)
            const formData: any = new FormData()
            formData.append('customer_id', customer.stripe_id)
            formData.append('portfolio_id', String(reduxCacheSiteportfolioid))
            formData.append('current_path', window.location.href)
            axios({
                data: formData,
                headers: refreshAxiosHeaders,
                method: 'post',
                url: postUrl,
            })
                .then((response) => {
                    if (process.env.NODE_ENV === 'development') console.log(response)
                    if (response.data.url) {
                        window.open(response.data.url, '_blank')
                    }
                    setPortalLoading(prev => ({ ...prev, [customer.id]: false }))
                })
                .catch((error) => {
                    setPortalLoading(prev => ({ ...prev, [customer.id]: false }))
                    axiosErrorHandlerSite({
                        apiUrl: postUrl,
                        component: 'TemplateBlock826HelperSite',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'handleStripePortal',
                    })
                })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'TemplateBlock826HelperSite',
                'handleStripePortal',
            ))
        }
    }

    function onHover(direction: string) {
        try {
            if (stylesNew?.hasHoverStyle) {
                if (direction === 'over') {
                    if (stylesNew.buttonStyle) {
                        const newnewStyle = Object.assign({}, stylesNew.buttonStyle)
                        if (stylesNew.hoverStyleBackground) newnewStyle.background = stylesNew.hoverStyleBackground
                        if (stylesNew.hoverStyleBorderColor) newnewStyle.borderColor = stylesNew.hoverStyleBorderColor
                        if (stylesNew.hoverStyleColor) newnewStyle.color = stylesNew.hoverStyleColor
                        setNewStyles(newnewStyle)
                    } else {
                        setNewStyles(undefined)
                    }
                } else {
                    if (stylesNew.buttonStyle) {
                        setNewStyles(stylesNew.buttonStyle)
                    } else {
                        setNewStyles(undefined)
                    }
                }
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'TemplateBlock826HelperSite',
                'onHover',
            ))
        }
    }

    return (
        <>
            <LinkHelperSite
                id={blockId}
                action='custom'
                className={`template-block-826${isEditHovered ? ' is-edit-hovered' : ''}${portalLoading[customer.id] ? ' disabled' : ''} ${deviceType}`}
                disabled={portalLoading[customer.id]}
                onClick={() => handleStripePortal(customer)}
                onCustomMouseLeave={() => onHover('leave')}
                onCustomMouseOver={() => onHover('over')}
                style={newStyles}
                to={undefined}
            >
                {portalLoading[customer.id]
                    ? 'Entrain de charger...'
                    : content?.stripe_customers?.length! > 1
                        ? `Accéder au portail Stripe - ${customer.email}`
                        : 'Accéder au portail Stripe'
                }
            </LinkHelperSite>
            <EditBlockSite
                isInComponent={isInComponent}
                mainParentId={mainParentId || object.id!}
                object={object}
                parentArray={parentArray}
                parentStyles={parentStyles}
            />
        </>
    )
})
